import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/Hooks";
import { toUnLoading } from "../../store/SiteSlice";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Card, Col, Row, Button, Typography, Space, Skeleton, Select } from "antd";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Link, useNavigate } from "react-router-dom";
import { dashboardService } from "../../services/DashboardService";
import { mdlDashboardResponse } from "../../models/service-models/dashboard/DashboardResponse";
import { PlusOutlined, SwapOutlined, CloseOutlined, ArrowRightOutlined, QuestionOutlined, SmileOutlined, QuestionCircleOutlined, QuestionCircleTwoTone, QuestionCircleFilled } from '@ant-design/icons';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { mdlHotel } from "../../models/domain/Hotel";
import { StatCard } from "../../components/dashboard/StatCard";
import AgencyCard from "../../components/dashboard/AgencyCard";
import CustomTooltip from "../../components/dashboard/CustomTooltip";
import { routeContants } from "../../utils/RouteConstants";

const { Title, Text } = Typography;
interface ChartDataItem {
  date: string;
  new: number;
  newNights: number;
  changed: number;
  changedNights: number;
  cancelled: number;
  cancelledNights: number;
}
const Dashboard = () => {
  const dispatch = useAppDispatch();
  const userHotels = useAppSelector(x => x.site.userHotels);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const nonPaymentHotels = userHotels?.filter(x => x.requirePayment === false) || [];
  const crossAgencies = useAppSelector(x => x.site.crossAgencies);
  const hotelUserPair = useAppSelector(x => x.site.hotelUserPairs);
  const [selectedHotels, setSelectedHotels] = useState<string[]>([]);

  useEffect(() => {
    dispatch(toUnLoading());
    if (nonPaymentHotels.length > 0 && selectedHotels.length === 0) {
      setSelectedHotels([nonPaymentHotels[0].paximumId.toString()]);
    }
  }, [nonPaymentHotels]);

  var data = null;
  var paymentRequiredHotels = _.filter(hotelUserPair, function (h) { return h.paymentRequired == true });
  if (paymentRequiredHotels && paymentRequiredHotels.length > 0) {
    var hotelsRequirePayment = _.filter(userHotels, function (h) { return paymentRequiredHotels.map(x => x.hotelId).includes(h.id) });
    if (hotelsRequirePayment && hotelsRequirePayment.length > 0) {
      data = hotelsRequirePayment;
    }
  }

  var upcomingPaymentHotels = userHotels?.filter(h => h.daysLeftToPaymentValidityDate && h.daysLeftToPaymentValidityDate >= 0 && h.daysLeftToPaymentValidityDate <= 10);

  const { data: dashboardData, isLoading } = useQuery<mdlDashboardResponse>({
    queryKey: ['dashboard', selectedHotels],
    queryFn: () => {
      const endDate = dayjs();
      const startDate = endDate.subtract(14, 'day');
      return dashboardService.getStats({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        days: 15,
        includeNights: true,
        agencyIds: (crossAgencies?.map(x => x.id) || []) as string[],
        paximumIds: selectedHotels.map(id => Number(id)),
      });
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: selectedHotels.length > 0
  });

  const handleDetailView = () => {
    navigate('/dashboard/detail');
  };

  if (isLoading || !dashboardData) {
    return (
      <div style={{
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        padding: '24px'
      }}>
        <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
          <Row gutter={[24, 24]}>
            {upcomingPaymentHotels && upcomingPaymentHotels.length > 0 &&
              <Col span={24}>
                <Card
                  bordered={false}
                  bodyStyle={{ padding: '24px' }}
                  style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                  <strong style={{ color: "#b84848" }}>{t("common.hotelsWithUpcomingPayment")}</strong>
                  <Row gutter={16}>
                    {upcomingPaymentHotels.map((hotel: mdlHotel, index: number) => (
                      <Col span={24} key={index}>
                        <h6>! {hotel.localName} : {hotel.daysLeftToPaymentValidityDate} {t("dashboard.days")}</h6>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </Col>
            }
            {data && (
              <Col span={24}>
                <Card
                  bordered={false}
                  bodyStyle={{ padding: '24px' }}
                  style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                  <strong style={{ color: "red" }}>{t("common.hotelsThatRequirePayment")}</strong>
                  <Row gutter={16}>
                    {data.map((hotel: mdlHotel, index: number) => (
                      <Col span={24} key={index}>
                        <h6>✗ {hotel.localName}</h6>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </Col>
            )}

            <Col span={18}>
              <Card
                bordered={false}
                bodyStyle={{ padding: '24px' }}
                style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
              >
                <Space direction="horizontal" style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space direction="vertical" size="small">
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                      {t('dashboard.lastFifteenDaysReport')}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                      {t('dashboard.reportDescription')}
                    </Text>
                  </Space>
                  <Space>
                    <Select
                      mode="multiple"
                      style={{ minWidth: '200px' }}
                      placeholder={t('common.selectHotels')}
                      value={selectedHotels}
                      onChange={setSelectedHotels}
                      options={nonPaymentHotels.map(hotel => ({
                        label: hotel.localName,
                        value: hotel.paximumId.toString()
                      }))}
                    />
                    <Button type="primary" onClick={handleDetailView}>
                      {t('dashboard.viewDetails')}
                    </Button>
                  </Space>
                </Space>
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px', backgroundColor: "#B1D9E3" }}>
                <Row>
                  <Col span={6}>
                    <QuestionCircleFilled style={{ fontSize: '48px' }} />
                  </Col>
                  <Col span={18}>
                    <h6>{t("dashboard.helpFile")}</h6>
                    <Link to={routeContants.HELP_USERMANUAL_USER_MANUAL}>{t("dashboard.hepFileExplanation")}</Link>
                  </Col>
                </Row>
              </Card>
            </Col>

            {[1, 2, 3, 4].map((_, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                  <Skeleton active paragraph={{ rows: 1 }} />
                </Card>
              </Col>
            ))}

            <Col span={24}>
              <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                <Skeleton active paragraph={{ rows: 8 }} />
              </Card>
            </Col>

            <Col span={24}>
              <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                <Skeleton active paragraph={{ rows: 1 }} />
                <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
                  {[1, 2, 3, 4].map((_, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <Card bordered={false} bodyStyle={{ padding: '20px' }} style={{ borderRadius: '16px' }}>
                        <Skeleton active avatar paragraph={{ rows: 3 }} />
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }





  const fillMissingDates = (data: ChartDataItem[]) => {
    const endDate = dayjs();
    const startDate = endDate.subtract(14, 'day');
    const filledData: ChartDataItem[] = [];
    let currentDate = startDate;

    while (currentDate.isSameOrBefore(endDate)) {
      const dateStr = currentDate.format('DD.MM.YYYY');
      const existingData = data.find(item => item.date === dateStr);

      filledData.push({
        date: dateStr,
        new: existingData?.new || 0,
        newNights: existingData?.newNights || 0,
        changed: existingData?.changed || 0,
        changedNights: existingData?.changedNights || 0,
        cancelled: existingData?.cancelled || 0,
        cancelledNights: existingData?.cancelledNights || 0
      });

      currentDate = currentDate.add(1, 'day');
    }

    return filledData;
  };
  const filledChartData = dashboardData ? fillMissingDates(dashboardData.chartData) : [];
  const sortedData = [...filledChartData].sort((a, b) =>
    dayjs(a.date, 'DD.MM.YYYY').unix() - dayjs(b.date, 'DD.MM.YYYY').unix()
  );
  return (
    <div style={{
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>


        <Row gutter={[24, 24]}>
          {upcomingPaymentHotels && upcomingPaymentHotels.length > 0 &&
            <Col span={24}>
              <Card
                bordered={false}
                bodyStyle={{ padding: '24px' }}
                style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
              >
                <strong style={{ color: "#b84848" }}>{t("common.hotelsWithUpcomingPayment")}</strong>
                <Row gutter={16}>
                  {upcomingPaymentHotels.map((hotel: mdlHotel, index: number) => (
                    <Col span={24} key={index}>
                      <h6>! {hotel.localName} : {hotel.daysLeftToPaymentValidityDate} {t("dashboard.days")}</h6>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          }
          {data && (
            <Col span={24}>
              <Card
                bordered={false}
                bodyStyle={{ padding: '24px' }}
                style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
              >
                <strong style={{ color: "red" }}>{t("common.hotelsThatRequirePayment")}</strong>
                <Row gutter={16}>
                  {data.map((hotel: mdlHotel, index: number) => (
                    <Col span={24} key={index}>
                      <h6>✗ {hotel.localName}</h6>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          )}
          <Col span={18}>
            <Card
              bordered={false}
              bodyStyle={{ padding: '24px' }}
              style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
              <Space direction="horizontal" style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space direction="vertical" size="small">
                  <Title level={4} style={{ margin: 0, color: '#262626' }}>
                    {t('dashboard.lastFifteenDaysReport')}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    {t('dashboard.reportDescription')}
                  </Text>
                </Space>
                <Space>
                  <Select
                    mode="multiple"
                    style={{ minWidth: '200px' }}
                    placeholder={t('common.selectHotels')}
                    value={selectedHotels}
                    onChange={setSelectedHotels}
                    options={nonPaymentHotels.map(hotel => ({
                      label: hotel.localName,
                      value: hotel.paximumId.toString()
                    }))}
                  />
                  <Button type="primary" onClick={handleDetailView}>
                    {t('dashboard.viewDetails')}
                  </Button>
                </Space>
              </Space>
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px', backgroundColor: "#B1D9E3" }}>
              <Row>
                <Col span={6}>
                  <QuestionCircleFilled style={{ fontSize: '48px' }} />
                </Col>
                <Col span={18}>
                  <h6>{t("dashboard.helpFile")}</h6>
                  <Link to={routeContants.HELP_USERMANUAL_USER_MANUAL}>{t("dashboard.hepFileExplanation")}</Link>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <StatCard
              title={`${t('dashboard.new')} ${t('dashboard.reservations').toLowerCase()}`}
              count={dashboardData.stats.new.count}
              nights={dashboardData.stats.new.nights}
              color="#52c41a"
              icon={<PlusOutlined style={{ fontSize: '24px', color: '#52c41a' }} />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title={`${t('dashboard.changed')} ${t('dashboard.reservations').toLowerCase()}`}
              count={dashboardData.stats.changed.count}
              nights={dashboardData.stats.changed.nights}
              color="#faad14"
              icon={<SwapOutlined style={{ fontSize: '24px', color: '#faad14' }} />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title={`${t('dashboard.cancelled')} ${t('dashboard.reservations').toLowerCase()}`}
              count={dashboardData.stats.cancelled.count}
              nights={dashboardData.stats.cancelled.nights}
              color="#ff4d4f"
              icon={<CloseOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title={`${t('dashboard.total')} ${t('dashboard.reservations').toLowerCase()}`}
              count={dashboardData.stats.total.count}
              nights={dashboardData.stats.total.nights}
              color="#722ed1"
              icon={<ArrowRightOutlined style={{ fontSize: '24px', color: '#722ed1' }} />}
            />
          </Col>

          <Col span={24}>
            <Card
              bordered={false}
              bodyStyle={{ padding: '24px' }}
              style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space direction="vertical" size="small">
                  <Title level={4} style={{ margin: 0, color: '#262626' }}>
                    {t('dashboard.reservationTrend')}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    {t('dashboard.chartDescription')}
                  </Text>
                </Space>
                <div style={{ height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={sortedData}
                      barGap={8}
                      barSize={20}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        fontSize={12}
                        tickFormatter={(value) => value.split('.').slice(0, 2).join('.')}
                        padding={{ left: 10, right: 10 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        fontSize={12}
                        width={50}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        iconType="circle"
                        iconSize={8}
                      />
                      <Bar
                        dataKey="new"
                        fill="#52c41a"
                        name={`${t('dashboard.new')} ${t('dashboard.reservations')}`}
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="changed"
                        fill="#faad14"
                        name={`${t('dashboard.changed')} ${t('dashboard.reservations')}`}
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="cancelled"
                        fill="#ff4d4f"
                        name={`${t('dashboard.cancelled')} ${t('dashboard.reservations')}`}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Space>
            </Card>
          </Col>

          <Col span={24}>
            <Card
              bordered={false}
              bodyStyle={{ padding: '24px' }}
              style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space direction="vertical" size="small">
                  <Title level={4} style={{ margin: 0, color: '#262626' }}>
                    {t('dashboard.agencyPerformance')}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    {t('dashboard.agencyDescription')}
                  </Text>
                </Space>
                <Row gutter={[24, 24]}>
                  {dashboardData.agencies.map((agency, index) => (
                    <AgencyCard agency={agency} index={index} />
                  ))}
                </Row>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Dashboard;